<template>
  <mi-dialog
    :close-icon="false"
    :model-value="true"
    class="combinations-dialog"
    full-width
    full-height
    no-esc-dismiss
    no-backdrop-dismiss
    seamless
  >
    <!-- Header -->
    <combinations-header
      v-model:show-results="layoutOptionsSelected"
      :is-table-saved="isTableSaved"
      :correlation-id="correlationId"
      :is-exporting="isTableDataExporting"
      :search-string="querySearchString"
      @update:is-table-saved="updateIsTableSavedValue"
      @click:auto-swap="autoSwapColumns"
      @click:export-data="exportTableData"
    >
      <mi-resize-observer @resize="handleCombinationsHeaderResize"></mi-resize-observer>
    </combinations-header>

    <!-- Table -->
    <combinations-table
      ref="combinationsTable"
      :is-table-saved="isTableSaved"
      :correlation-id="correlationId"
      :show-results="layoutOptionsSelected"
      class="combinations-dialog__table"
      @update:is-table-saved="updateIsTableSavedValue"
    ></combinations-table>
  </mi-dialog>
</template>

<script>
  import { useStore } from 'vuex'
  import { exportFile } from 'quasar'

  import { exportVariantTreeTable, getVariantTreeCombination } from '@/api'
  import recordAnalytics from '@/utils/analytics/recordAnalytics'
  import { VM_AUTO_SWAP_COLUMN, VM_COMB_TABLE_EXPORT } from '@/utils/analytics/constants'

  import { buildExcelName } from '@/components/saved-tables/utils'
  import { computed, onMounted, ref } from 'vue'
  import { useRoute } from 'vue-router'
  import CombinationsHeader from './combinations-header/CombinationsHeader.vue'
  import CombinationsTable from './combinations-table/CombinationsTable.vue'
  import { useCombinationsOptions } from './utils/composables/useCombinationsOptions'

  export default {
    name: 'Combinations',
    components: {
      CombinationsTable,
      CombinationsHeader
    },
    props: {
      correlationId: {
        type: String,
        required: true
      }
    },
    setup(props) {
      const querySearchString = ref('')
      const route = useRoute()
      const isTableSaved = ref(true)

      const updateIsTableSavedValue = () => {
        isTableSaved.value = !isTableSaved.value
      }

      const store = useStore()
      const resultsFilterExcel = computed(() => store.state.search.resultsFilterExcel)
      const {
        combinationsOptions,
        filtersAsRequestParams,
        sortersAsRequestParams,
        aliasesAsRequestParams
      } = useCombinationsOptions()

      const isTableDataExporting = ref(false)
      const layoutOptionsSelected = ref({ ambiguous: true, incomplete: true, valid: true })
      const combinationsTable = ref(null)

      const autoSwapColumns = () => {
        combinationsOptions.isCombinationsTableLoading = true

        try {
          combinationsTable.value?.refreshTabulator()

          // Analytics
          recordAnalytics(VM_AUTO_SWAP_COLUMN)
        }
        catch {
          combinationsOptions.isCombinationsTableLoading = false
        }
      }

      const checkIfTableSaved = async correlationId => {
        const response = await getVariantTreeCombination(correlationId)
        isTableSaved.value = response.showLater
      }

      const handleCombinationsHeaderResize = ({ height } = {}) => {
        const combinationsDialogEl = document.querySelector('.combinations-dialog')
        const pxHeight = `${ height }px`

        combinationsDialogEl?.style?.setProperty('--combinations-header-height', pxHeight)
      }

      const buildVMTExcelName = (tableName = '') => {
        const { query } = route

        const pp = query?.pp ? atob(query.pp) : ''
        const pmName = query?.productModelBusinessName ? atob(query.productModelBusinessName) : ''

        const excelName = buildExcelName(tableName, pp, pmName)

        return excelName
      }

      const exportTableData = async (tableName = '') => {
        const { showCodeId, showName, splitCodeIdAndName } = resultsFilterExcel.value
        isTableDataExporting.value = true

        try {
          const excelFile = await exportVariantTreeTable({
            showCodeId,
            showName,
            splitCodeIdAndName,
            showAmbiguous: layoutOptionsSelected.value.ambiguous,
            showIncomplete: layoutOptionsSelected.value.incomplete,
            showValidResults: layoutOptionsSelected.value.valid,
            filters: filtersAsRequestParams(),
            sorters: sortersAsRequestParams(),
            aliases: aliasesAsRequestParams(),
            correlationId: props.correlationId
          })

          const vmExcelName = buildVMTExcelName(tableName)

          exportFile(vmExcelName, new Blob([excelFile]))

          // Analytics
          recordAnalytics(VM_COMB_TABLE_EXPORT)
        }
        finally {
          isTableDataExporting.value = false
        }
      }

      onMounted(() => {
        const { name, query } = route
        if (name === 'search' && query.searchString) {
          querySearchString.value = query.searchString
        }

        checkIfTableSaved(props.correlationId)
      })

      return {
        handleCombinationsHeaderResize,
        exportTableData,
        layoutOptionsSelected,
        isTableDataExporting,
        querySearchString,
        autoSwapColumns,
        combinationsTable,
        buildVMTExcelName,
        combinationsOptions,
        isTableSaved,
        updateIsTableSavedValue
      }
    }
  }
</script>

<style lang="scss">
  $combinations-dialog-flex-gap: 1.5rem;

  .combinations-dialog {
    --combinations-header-height: 40px;
  }

  .combinations-dialog .q-dialog__inner {
    padding: 0;

    .mi-card__section {
      display: flex;
      flex-direction: column;
      gap: $combinations-dialog-flex-gap;
      min-width: 1200px;
      height: 90%;
      padding-top: 1.5rem;
      padding-bottom: 1.5rem;
    }
  }

  .combinations-dialog__table {
    height: calc(100% - var(--combinations-header-height) - #{$combinations-dialog-flex-gap});
  }
</style>
